/**
 * useAnnouncements — the active planned-outage notices for the current user.
 *
 * Owns three things the display components shouldn't have to:
 *
 * 1. **Polling.** Every few minutes, so a notice published mid-session
 *    appears without a reload.
 *
 * 2. **A ticking clock.** The phase is derived from timestamps, so the banner
 *    has to re-render as time passes even when nothing is fetched. Without
 *    this, an "upcoming" banner would sit there saying "starts in 2 hours"
 *    for the rest of the afternoon — which is worse than saying nothing,
 *    because people plan around it.
 *
 * 3. **An offline cache.** This is the part that earns its keep. The whole
 *    feature is about outages, and during one the API is exactly what stops
 *    answering — so a fetch failure must NOT clear the banner. The last good
 *    response is mirrored into localStorage and used as the fallback, which
 *    means a window announced while things were healthy keeps escalating and
 *    stays on screen once they aren't.
 *
 *    The honest limit: this only covers windows announced BEFORE the API went
 *    away. A genuinely unplanned outage can't be reported through a channel
 *    that is itself down — that needs email or a status page off this stack.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AppContext } from "../AppContextProvider";
import appSupportService from "../services/appSupportService";
import { isVisible } from "../utils/announcementPhase";

// How often to refetch. Slower than the notification bell's 45s: an outage
// notice is authored hours or days ahead, so there is nothing to gain from
// polling hard, and this request is made by every session in the company.
const POLL_MS = 5 * 60 * 1000;

// How often to re-evaluate the phase against the clock. One minute is enough
// to make "starts in N minutes" honest, and to flip upcoming -> imminent ->
// active within a minute of the real boundary.
const TICK_MS = 60 * 1000;

const CACHE_KEY = "bd.announcements.active";

/** Read the cached active set. Never throws — a bad cache is just no cache. */
export const readCache = () => {
  try {
    const raw = window.localStorage?.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Mirror the active set to localStorage. Never throws (quota, private mode). */
export const writeCache = (rows) => {
  try {
    window.localStorage?.setItem(CACHE_KEY, JSON.stringify(rows || []));
  } catch {
    // A cache we can't write is a cache we do without.
  }
};

export const useAnnouncements = () => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  // Seed from cache so a banner paints on first render rather than after a
  // round trip — and so it paints at all when the API is already down.
  const [announcements, setAnnouncements] = useState(() => readCache());
  const [now, setNow] = useState(() => Date.now());
  // True once a fetch has succeeded this session. Until then we're showing
  // cached data, which the admin screen has no business trusting.
  const [loaded, setLoaded] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      const { token: tk, userOid } = await getAuth();
      if (!tk) return;
      const resp = await appSupportService.listActiveAnnouncements({
        token: tk,
        userOid,
      });
      const rows = resp?.data?.data || [];
      writeCache(rows);
      if (mountedRef.current) {
        setAnnouncements(rows);
        setLoaded(true);
      }
    } catch (err) {
      // Deliberately NOT clearing state. During the very outage this feature
      // exists to announce, this request is the one that fails — dropping the
      // banner at that moment would be the worst possible behaviour.
      console.error(
        "Failed to load announcements (keeping cached copy):",
        err?.message || err
      );
    }
  }, [getAuth]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => {
      if (mountedRef.current) setNow(Date.now());
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  /**
   * Record a banner dismissal for one phase.
   *
   * Optimistic: the banner must disappear under the click, not after a round
   * trip. The server response is not merged back — the next poll carries the
   * authoritative state, and a failed write simply means the banner returns,
   * which is the safe direction to fail for an outage notice.
   */
  const dismiss = useCallback(
    async (announcement, phase) => {
      setAnnouncements((prev) => {
        const next = prev.map((a) =>
          a.id === announcement.id ? { ...a, dismissed_phase: phase } : a
        );
        writeCache(next);
        return next;
      });
      try {
        const { token: tk, userOid } = await getAuth();
        await appSupportService.dismissAnnouncement({
          token: tk,
          userOid,
          id: announcement.id,
          phase,
        });
      } catch (err) {
        console.error("Failed to dismiss announcement:", err?.message || err);
      }
    },
    [getAuth]
  );

  /** Record a modal acknowledgement. Permanent; same optimistic pattern. */
  const acknowledge = useCallback(
    async (announcement) => {
      setAnnouncements((prev) => {
        const next = prev.map((a) =>
          a.id === announcement.id ? { ...a, acknowledged: true } : a
        );
        writeCache(next);
        return next;
      });
      try {
        const { token: tk, userOid } = await getAuth();
        await appSupportService.acknowledgeAnnouncement({
          token: tk,
          userOid,
          id: announcement.id,
        });
      } catch (err) {
        console.error(
          "Failed to acknowledge announcement:",
          err?.message || err
        );
      }
    },
    [getAuth]
  );

  // Drop anything that has aged out of its window since it was fetched (or
  // cached) — an expired notice must not linger just because nobody polled.
  const live = announcements.filter((a) => isVisible(a, now));

  return { announcements: live, now, loaded, refresh, dismiss, acknowledge };
};

export default useAnnouncements;
